import type { FleetStatusField } from '@immo/api-contract';

type Props = { readonly fields: readonly FleetStatusField[] };

/**
 * The generic `{label, value, href?}` renderer (ADR 0047). NO switch on
 * `label`, no map of known labels, no per-field formatting — an agent-reported
 * field the page has never seen renders exactly like one it has. `href`
 * becomes a link only when its scheme is http(s): defence in depth beside the
 * contract's own refusal, on the one property of the value (not the label)
 * that is safe to branch on.
 *
 * Below `sm` it is one column with the value UNDER its label, and the value
 * breaks rather than truncates: a `worktree` path or a branch name is longer
 * than any column a label leaves over at 390px, and half a path answers
 * nothing. That is responsive classes on this one component, never a second
 * renderer — two code paths for one contract is the first place a per-label
 * special case moves in (design run batch 3, §6).
 */
const HTTP_URL = /^https?:\/\//i;

export function StatusFields({ fields }: Props) {
  if (fields.length === 0) return null;

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 sm:gap-y-2">
      {fields.map((field, index) => (
        <div
          key={`${index}-${field.label}`}
          className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2"
        >
          <dt className="text-xs text-text-tertiary sm:w-24 sm:shrink-0 sm:truncate sm:text-sm">{field.label}</dt>
          {field.href !== undefined && HTTP_URL.test(field.href) ? (
            <dd className="min-w-0 break-words font-mono text-base-sm text-interactive underline underline-offset-2">
              <a href={field.href} target="_blank" rel="noreferrer noopener">
                {field.value}
              </a>
            </dd>
          ) : (
            <dd className="min-w-0 break-words font-mono text-base-sm text-text-primary">{field.value}</dd>
          )}
        </div>
      ))}
    </dl>
  );
}
